import { useEffect, useRef, useState } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/Logo';
import { FRAME_END } from '@/lib/heroFrames';
import ThemeLangControls from '@/components/ThemeLangControls';
import { useLang } from '@/hooks/useLang';
import { pick } from '@/lib/localized';
import type { Localized } from '@/lib/localized';
import {
  CATEGORIES,
  FOCUS_SLUGS,
  getProgram,
  programsOf,
} from '@/data/programs';

type Link2 = { to: string; label: string };

// "Kapsül Destek Programları" seçilince sağ panelde logolu kartlar gösterilir.
const SUPPORT_PROGRAMS: {
  logo: string;
  label: Localized;
  accent: string;
  to: string;
  /** Logonun İÇİNDE program adı yazıyor → altına ayrıca etiket basılmaz. */
  wordmark?: boolean;
}[] = [
  {
    // Yazılı yatay logo (amblem + "TERMINAL"). Zaten açık renkli (#f5f2f5),
    // koyu menüde olduğu gibi kullanılır.
    logo: '/terminal-yatay.svg',
    label: { tr: 'Terminal Çağrı Programı', en: 'Terminal Call Program' },
    accent: '#fe6a00',
    to: '/program/terminal-call',
    wordmark: true,
  },
  {
    // Eklem'in yazılı logosu açık zemin için tasarlı (yazı siyah, gövde
    // #140047); koyu menüde okunması için beyaz varyantı kullanılır —
    // marka yeşili (#46c68c) korunur.
    logo: '/eklem-yatay-beyaz.svg',
    label: {
      tr: 'Eklem Topluluk Destek Programı',
      en: 'Eklem Community Support Program',
    },
    accent: '#46c68c',
    to: '/community',
    wordmark: true,
  },
  {
    // Bu da söz logosu (720×104, isim logonun içinde) → alt etiket basılmaz.
    logo: '/next-level.svg',
    label: {
      tr: 'Next Level / Sürdürülebilirlik Programı',
      en: 'Next Level / Sustainability Program',
    },
    accent: '#fd6a00',
    to: '/program/next-level',
    wordmark: true,
  },
];
// Program slug'ı → /public logosu. Logosu olan programlar (İş Birliği vb.)
// mega-menüde Destek Programları kartlarıyla aynı stilde (logo + isim) gösterilir.
const PROGRAM_LOGOS: Record<string, string> = {
  engineerhub: '/engineerhub.svg',
  // Koyu menüde okunsun diye BEYAZ varyant (bistlab.svg'nin lacivert
  // #01193f gövdesi koyu zeminde kayboluyordu; teal #0093ac korunur).
  bistlab: '/bistlab-white.svg',
  'rota-tech-development': '/rota-lacivert.svg',
  // Koyu menüde okunsun diye koyu-zemin varyantı: kapsam-yatay.svg'nin
  // laciverti (#0C3455) koyu zeminde kayboluyordu; aynı tonun açık hâli
  // (#4e9bda) kullanılır, gri amblem korunur (su-hackathonu-dark ile aynı yol).
  'defense-industry-development': '/kapsam-yatay-dark.svg',
  'tasting-workshops': '/tadimlik-atolyeler.svg',
  // Koyu menüde okunsun diye BEYAZ varyant.
  'prototype-acceleration': '/prototip-hizlandirma-beyaz.svg',
  // NOT: 'agriculture-ideathon' bilerek YOK — Odak Alanları'ndaki
  // "Tarım Teknolojileri" kartı ideathon etkinliğinden daha geniş bir
  // başlıktır; ideathon SÖZ LOGOSUYLA değil, yalnızca amblemle işaretlenir.
  'agriculture-technologies': '/tarim-ideathonu-favicon.svg',
  'agriculture-workshop': '/tarim-calistayi.svg',
};

// Logosu SÖZ LOGOSU olmayan (yalnızca amblem) programlar: amblemin yanında
// program adı da yazılır, aksi hâlde hangi program olduğu anlaşılmaz.
const LOGO_WITH_LABEL = ['agriculture-technologies'];

// Yarışma başvuruları dış portalı (2027 CTA) — site içi rota DEĞİL.
const TEAM_APPLY_URL = 'https://terminal.kapsul.org.tr/';

const NAV_LOGO_IMAGE_PROPS = {
  loading: 'eager' as const,
  decoding: 'async' as const,
  fetchPriority: 'high' as const,
};

// "Ideathon" seçilince sağ panelde gösterilen sektör ideathonları (alt kartlar).
const IDEATHONS = [
  {
    to: '/program/agriculture-ideathon',
    label: { tr: 'Tarım İdeathonu', en: 'Agriculture Ideathon' },
    logo: '/tarim-ideathonu.svg',
  },
  {
    // Emlak Konut logosu markanın kendisi olduğu için yanında program adı da
    // yazılır (withLabel) — diğer kartlarda logo tek başına yeterlidir.
    to: '/program/real-estate-ideathon',
    label: { tr: 'Emlak Konut İdeathonu', en: 'Emlak Konut Ideathon' },
    logo: '/organizers/emlak-konut.svg',
    withLabel: true,
  },
];

// "Hackathon" seçilince gösterilen hackathonlar (alt kartlar).
const HACKATHONS = [
  {
    to: '/program/water-hackathon',
    label: { tr: 'Su Hackathonu', en: 'Water Hackathon' },
    // Koyu menüde okunsun diye açık tonlu varyant.
    logo: '/su-hackathonu-dark.svg',
  },
];

// "Çalıştay" seçilince gösterilen çalıştaylar (alt kartlar).
const WORKSHOPS = [
  {
    to: '/program/agriculture-workshop',
    label: { tr: 'Tarım Çalıştayı', en: 'Agriculture Workshop' },
    logo: '/tarim-calistayi.svg',
  },
];

// Ekosistem menüsü öğesi → içine açılan sektör alt kartları.
const SUBCARDS: Record<
  string,
  {
    to: string;
    label: { tr: string; en: string };
    logo?: string;
    withLabel?: boolean;
  }[]
> = {
  '/program/ideathon': IDEATHONS,
  '/program/hackathon': HACKATHONS,
  '/program/workshop': WORKSHOPS,
};

// Mega-menü alt öğesi: solda etiket, sağ panelde açıklama + "Devamını oku".
type MenuItem = { to: string; label: string; desc: string };
type Menu = { key: string; label: string; items: MenuItem[] };

const Navbar = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false); // mobil tam-ekran overlay
  const [scrolled, setScrolled] = useState(false);
  // Navbar açık zeminli (ör. beyaz orb sahnesi) bir bölümün üzerinde mi?
  // True ise: açık cam ada + koyu yazı → menü okunur kalır (global tema koyu olsa bile).
  const [onLight, setOnLight] = useState(false);
  // Masaüstü mega-menü: açık olan üst başlık ve panelde vurgulu alt öğe.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // Mobil overlay: açık akordeon bölümü (masaüstü mega-menünün mobil karşılığı).
  const [mobileKey, setMobileKey] = useState<string | null>('programs');
  // Mobil: kart ızgarası olan alt öğelerin (destek/işbirliği/projeler)
  // açık olanı — onlar da kendi içinde açılır/kapanır.
  const [mobileSubKey, setMobileSubKey] = useState<string | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  // Ana sayfa frame (ScrollFrames) bölgesine göre navbar durumu:
  //   'top'     → sayfanın en tepesi: tam navbar.
  //   'inFrame' → frame kaydırılırken: navbar küçülüp yalnızca logoya iner.
  //   'past'    → frame geçildi (veya diğer sayfalar): tam navbar.
  const isHome = location.pathname === '/';
  const [frameState, setFrameState] = useState<'top' | 'inFrame' | 'past'>(
    'top',
  );
  // Logo durumundayken üzerine gelinince (kısa beklemeyle) tekrar açılır.
  const [hovered, setHovered] = useState(false);
  const hoverTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);

      // Navbar bandının (üst ~48px) altındaki bölüm açık zeminli mi?
      let light = false;
      document.querySelectorAll('[data-nav-light]').forEach((el) => {
        const r = (el as HTMLElement).getBoundingClientRect();
        if (r.top <= 48 && r.bottom >= 48) light = true;
      });
      setOnLight(light);

      if (!isHome) return setFrameState('past');
      if (y < 10) return setFrameState('top');
      const frame = document.querySelector('[data-scroll-frames]');
      if (!frame) return setFrameState('past');
      const rect = (frame as HTMLElement).getBoundingClientRect();
      // frame bölümünün altı navbar'ın altına çıkınca "geçildi" sayılır.
      if (rect.bottom <= 80) return setFrameState('past');
      // Logo (KapsulMark) belirmeye başlarken navbar AÇILIR → logo bardan
      // çıkıyormuş gibi görünür (eskiden fp≈0.8'de açılıyordu, geç kalıyordu).
      const total = rect.height - window.innerHeight;
      const fp = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      // Eşik FRAME_END ile ortak: kareler bitmeden navbar 'geçildi'
      // durumuna düşüp erkenden açılmasın.
      setFrameState(fp >= FRAME_END ? 'past' : 'inFrame');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isHome]);

  // Frame bölgesinden çıkınca hover bayrağını sıfırla.
  useEffect(() => {
    if (frameState !== 'inFrame') setHovered(false);
  }, [frameState]);

  // Navbar açık mı? Frame içinde DEĞİLSE her zaman açık; frame içindeyse yalnızca
  // üzerine gelinmişse (hovered) açılır.
  const expanded = frameState !== 'inFrame' || hovered;

  // Site kalıcı koyu temada; beyaz logo + açık menü yazıları koyu zemine göre
  // tasarlı. Beyaz orb sahnesi üzerine gelince adaya KOYU frosted zemin ver →
  // "arkası koyu" koşulu geri gelir, logo/menü/dil pili okunur kalır.
  const islandSurface = onLight
    ? 'bg-slate-900/70'
    : 'bg-white/20 dark:bg-white/[0.07]';
  // Açılır mega-menü paneli de aynı uyumla koyulaşsın (beyaz orb üzerinde).
  const dropdownSurface = onLight
    ? 'border-white/10 bg-slate-900/85'
    : 'border-white/15 bg-white/[0.08]';

  // Özel temalı sayfalarda cam ada/panel, sayfanın vurgu rengiyle HAFİFÇE
  // tonlanır (footer'daki accent haritasıyla aynı yollar) → kurumsal bütünlük.
  const PAGE_TINTS: Record<string, string> = {
    '/program/terminal-call': '#fe6a00',
    '/community': '#46c68c',
    '/program/next-level': '#fd6a00',
    '/program/engineerhub': '#ff8500',
    '/program/bistlab': '#0093ac',
    '/program/water-hackathon': '#22b8d4',
    '/program/rota-tech-development': '#6f76d8',
    '/program/tasting-workshops': '#f5a524',
    '/program/prototype-acceleration': '#ffb600',
    '/program/defense-industry-development': '#4e9bda',
    '/program/mentor-pool': '#3d84ff',
    '/mentorship': '#3d84ff',
    '/program/agriculture-ideathon': '#D79328',
    '/program/agriculture-technologies': '#D79328',
    '/program/academy': '#f2b53c',
    '/program/smart-city': '#38bdf8',
    '/program/project-capacity-development': '#8b7cf8',
    '/program/entrepreneurship': '#46c68c',
    '/program/agriculture-workshop': '#8DC63F',
    '/institutional': '#2ec9b8',
    '/radar': '#BE1E2D',
  };
  const tint = onLight
    ? undefined
    : (PAGE_TINTS[location.pathname] ??
      (location.pathname.startsWith('/events')
        ? '#3d84ff'
        : location.pathname.startsWith('/press')
          ? '#7ea6ff'
          : undefined));
  const tintIsland = tint
    ? ({
        background: `linear-gradient(135deg, ${tint}1f 0%, ${tint}08 45%, rgba(10,9,12,0.38) 100%)`,
      } as React.CSSProperties)
    : undefined;
  const tintPanel = tint
    ? ({
        background: `linear-gradient(160deg, ${tint}17 0%, rgba(11,10,14,0.62) 55%)`,
        borderColor: `${tint}33`,
      } as React.CSSProperties)
    : undefined;

  // Logo kapsülünün üzerine gelince "bir süre kalınca" açılması için kısa bekleme.
  const onLogoEnter = () => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHovered(true), 280);
  };
  // Fare adadan ayrılınca: tekrar logoya in + mega menüyü kapat.
  const onIslandLeave = () => {
    window.clearTimeout(hoverTimer.current);
    setHovered(false);
    scheduleClose();
  };

  // Mobil açılır menü: ESC ile kapat (dropdown olduğu için scroll kilidi yok).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Rota değişince tüm menüleri kapat.
  useEffect(() => {
    setOpen(false);
    setOpenKey(null);
    setMobileKey('programs');
    setMobileSubKey(null);
  }, [location.pathname]);

  const discover: Link2[] = [
    { to: '/events', label: t('nav.events') },
    { to: '/press', label: t('nav.press') },
  ];

  // "Mentor Havuzu" kategorisi — Programlar menüsünde doğrudan (hazır) program
  // sayfasına (/program/mentor-pool) götüren bir öğe olarak gösterilir.
  const mentorCat = CATEGORIES.find((c) => c.slug === 'mentorship');

  // Odak Alanları kartları — başlıklar veriden gelir (dile göre doğru çevirir).
  const focusPrograms = FOCUS_SLUGS.map((slug) => {
    const p = getProgram(slug);
    return {
      slug,
      to: `/program/${slug}`,
      label: p ? pick(p.title, lang) : slug,
    };
  });

  // Masaüstü mega-menü yapısı (üst başlık → alt kategoriler + açıklamalar).
  const menus: Menu[] = [
    {
      key: 'programs',
      label: t('nav.programs'),
      // Sıra ELLE belirlenir: Destek → Gelişim → Odak Alanları → Mentor
      // Havuzu → Tamamlanan Projeler.
      items: [
        ...['support', 'development'].map((slug) => {
          const c = CATEGORIES.find((x) => x.slug === slug);
          return {
            to: `/programs/${slug}`,
            label: c ? pick(c.title, lang) : slug,
            desc: c ? pick(c.subtitle, lang) : '',
          };
        }),
        {
          to: '/programs/focus',
          label: lang === 'en' ? 'Focus Areas' : 'Odak Alanları',
          desc:
            lang === 'en'
              ? 'Themed collaboration programs: finance, defense industry and agriculture technologies.'
              : 'Temalı iş birliği programları: finans, savunma sanayii ve tarım teknolojileri.',
        },
        {
          to: '/program/mentor-pool',
          label: mentorCat ? pick(mentorCat.title, lang) : 'Mentor Havuzu',
          desc: mentorCat
            ? pick(mentorCat.subtitle, lang)
            : lang === 'en'
              ? 'A digital mentoring system matching teams with expert mentors.'
              : 'Ekipleri alanında uzman mentörlerle eşleştiren dijital mentorluk sistemi.',
        },
        ...['projects'].map((slug) => {
          const c = CATEGORIES.find((x) => x.slug === slug);
          return {
            to: `/programs/${slug}`,
            label: c ? pick(c.title, lang) : slug,
            desc: c ? pick(c.subtitle, lang) : '',
          };
        }),
      ],
    },
    {
      key: 'ecosystem',
      label: lang === 'en' ? 'Ecosystem Development' : 'Ekosistem Geliştirme',
      items: [
        {
          to: '/program/hackathon',
          label: 'Hackathon',
          desc:
            lang === 'en'
              ? 'Production-focused hackathon marathons.'
              : 'Üretim odaklı hackathon maratonları.',
        },
        {
          to: '/program/ideathon',
          label: 'Ideathon',
          desc:
            lang === 'en'
              ? 'Sector competitions from idea to solution.'
              : 'Fikirden çözüme sektör yarışmaları.',
        },
        {
          to: '/program/workshop',
          label: lang === 'en' ? 'Workshop' : 'Çalıştay',
          desc:
            lang === 'en'
              ? 'Multi-stakeholder sessions shaping common ground.'
              : 'Paydaşları ortak bilgi zemininde buluşturan oturumlar.',
        },
      ],
    },
    {
      key: 'discover',
      label: t('nav.discover'),
      items: [
        { ...discover[0], desc: t('nav.eventsDesc') },
        { ...discover[1], desc: t('nav.pressDesc') },
        {
          to: '/radar',
          label: 'Kapsül Radar',
          desc:
            lang === 'en'
              ? 'The content series tracking tech and team achievements.'
              : 'Teknolojiyi ve başarıları görünür kılan içerik serisi.',
        },
      ],
    },
  ];

  // Kapsül Gelişim Programları → alt grup (Yetenek / Yetkinlik) başlıklarıyla
  // program listesi (hem masaüstü panelde hem mobil menüde).
  const devPrograms = programsOf('development');
  const devGroups = Array.from(
    new Set(devPrograms.map((p) => (p.group ? pick(p.group, lang) : ''))),
  ).filter(Boolean);

  // Hover ile aç/kapat (panele geçerken kapanmaması için kısa gecikme).
  const openMenu = (key: string) => {
    window.clearTimeout(closeTimer.current);
    setActiveIdx(0);
    setOpenKey(key);
  };
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenKey(null), 130);
  };
  const cancelClose = () => window.clearTimeout(closeTimer.current);

  const activeMenu = menus.find((m) => m.key === openKey) ?? null;
  const activeItem = activeMenu?.items[activeIdx] ?? activeMenu?.items[0];

  return (
    <>
      {/* ── Glass "ada" üst bar ──
          Header şeffaf/akışta; görsel yüzey içteki yüzen cam adada. h-20 sabit
          yükseklik → Home full-bleed hero hizası (-mt-20) ve diğer sayfa üst
          boşlukları deterministik. */}
      {/* Güvenli alan payı YOK: sayfa içeriği durum çubuğunun ALTINA kadar
          uzansın (viewport-fit=cover ile tam kenar-kenara görünüm). */}
      <header className="sticky top-0 z-40 h-20">
        <div className="mx-auto flex h-full max-w-6xl items-start px-4 pt-2 sm:px-6 sm:pt-5">
          <div className="relative w-full" onMouseLeave={onIslandLeave}>
            {/* Tam ada — frame içinde (açık değilken) sol köşeye doğru küçülerek
                solar; logo kapsülü onun yerini alır. */}
            <div
              className={`origin-top transition-all duration-300 ${
                expanded
                  ? 'scale-100 opacity-100'
                  : 'pointer-events-none scale-90 opacity-0'
              }`}
            >
              {/* Ada */}
              <div
                style={tintIsland}
                className={`relative flex items-center justify-between gap-4 overflow-hidden rounded-full ${islandSurface} py-2 pl-5 pr-2 backdrop-blur-2xl backdrop-saturate-[1.8] transition-all duration-300 ${
                  scrolled
                    ? 'shadow-[inset_0_-8px_24px_-12px_rgba(255,255,255,0.18),0_16px_44px_-12px_rgba(0,0,0,0.5)]'
                    : 'shadow-[inset_0_-8px_24px_-12px_rgba(255,255,255,0.14),0_10px_32px_-12px_rgba(0,0,0,0.35)]'
                }`}
              >
                {/* Glass kenar: yumuşak parıltı SOL ÜST ve SAĞ ALT köşelerde,
                  ortada söner. Saf beyaz değil — arka plana yakın mavimsi,
                  yansıma/blur hissi veren ince çerçeve (135° + mask). */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    padding: 1,
                    background:
                      'linear-gradient(135deg, rgba(206,226,255,0.5) 0%, rgba(206,226,255,0.12) 18%, transparent 40%, transparent 60%, rgba(206,226,255,0.1) 82%, rgba(206,226,255,0.42) 100%)',
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
                <Logo />

                {/* Masaüstü gezinme */}
                <nav className="hidden items-center gap-0.5 lg:flex">
                  {/* Kurumsal — gezinmenin BAŞINDA, Programlar'dan önce */}
                  <Link
                    to="/institutional"
                    className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-azure/10 hover:text-azure dark:text-slate-200 dark:hover:bg-azure/15 dark:hover:text-azure"
                  >
                    {t('nav.corporate')}
                  </Link>
                  {menus.map((m) => {
                    const isOpen = openKey === m.key;
                    return (
                      <span key={`w-${m.key}`} className="contents">
                        <button
                          key={m.key}
                          type="button"
                          onMouseEnter={() => openMenu(m.key)}
                          onClick={() =>
                            setOpenKey((k) => (k === m.key ? null : m.key))
                          }
                          aria-expanded={isOpen}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                            isOpen
                              ? 'bg-azure/10 text-azure dark:bg-azure/15 dark:text-azure'
                              : 'text-slate-700 hover:bg-azure/10 hover:text-azure dark:text-slate-200 dark:hover:bg-azure/15 dark:hover:text-azure'
                          }`}
                        >
                          {m.label}
                          <DownOutlined
                            className={`text-[10px] transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </span>
                    );
                  })}
                </nav>

                {/* Sağ: tema/dil + iletişim CTA + mobil menü */}
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Tema/dil: sm+ her zaman; mobilde menü açıkken adada görünür */}
                  <div className={`${open ? 'block' : 'hidden'} sm:block`}>
                    <ThemeLangControls />
                  </div>
                  <a
                    href={TEAM_APPLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="group hidden items-center gap-1.5 rounded-full border-2 border-kred bg-kred/[0.06] px-3.5 py-1.5 transition-all hover:-translate-y-0.5 hover:border-kred hover:bg-kred/10 lg:inline-flex"
                  >
                    <span className="text-[20px] font-extrabold leading-none tracking-tight text-kred">
                      {t('nav.applyYear')}
                    </span>
                    <span className="flex flex-col text-[9px] font-bold uppercase leading-[1.15] tracking-wide text-kred">
                      {t('nav.applyTitle')
                        .split(' ')
                        .map((w) => (
                          <span key={w}>{w}</span>
                        ))}
                    </span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={t('nav.menu')}
                    className="inline-flex items-center justify-center p-1 lg:hidden"
                  >
                    <span className="flex h-7 w-7 flex-col items-center justify-center gap-[3px] rounded-full bg-gradient-to-br from-azure to-[#5f9bff]">
                      <span className="h-0.5 w-3.5 rounded-full bg-white" />
                      <span className="h-0.5 w-3.5 rounded-full bg-white" />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Logo kapsülü — frame kaydırılırken görünen küçük "logo" durumu.
                Üzerine gelip bir süre kalınca tam navbar açılır; fare çekilince
                (onIslandLeave) yeniden bu logoya iner. */}
            <div
              className={`absolute left-1/2 top-0 -translate-x-1/2 transition-all duration-300 ${
                expanded
                  ? 'pointer-events-none scale-110 opacity-0'
                  : 'scale-100 opacity-100'
              }`}
              onMouseEnter={onLogoEnter}
              onClick={() => setHovered((v) => !v)}
            >
              <div className="relative flex items-center overflow-hidden rounded-full bg-white/20 px-4 py-2 shadow-[inset_0_-8px_24px_-12px_rgba(255,255,255,0.14),0_10px_32px_-12px_rgba(0,0,0,0.35)] backdrop-blur-2xl backdrop-saturate-[1.8] dark:bg-white/[0.07]">
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    padding: 1,
                    background:
                      'linear-gradient(135deg, rgba(206,226,255,0.5) 0%, rgba(206,226,255,0.12) 18%, transparent 40%, transparent 60%, rgba(206,226,255,0.1) 82%, rgba(206,226,255,0.42) 100%)',
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
                <Logo />
              </div>
            </div>

            {/* ── Mega-menü paneli (masaüstü) ──
                Sol: alt kategori listesi (hover → sağ panel güncellenir).
                Sağ: seçili öğenin başlığı + açıklaması + "Devamını oku". */}
            {activeMenu && activeItem && (
              <div
                className="absolute left-0 top-full z-50 hidden w-full max-w-3xl pt-3 lg:block"
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                <div
                  style={tintPanel}
                  className={`animate-pagefade grid grid-cols-[16rem_minmax(0,1fr)] gap-2 rounded-[1.25rem] border ${dropdownSurface} p-2.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),0_24px_60px_-16px_rgba(0,0,0,0.65)] backdrop-blur-2xl backdrop-saturate-150`}
                >
                  {/* Sol liste */}
                  <ul className="flex flex-col gap-0.5">
                    {activeMenu.items.map((it, i) => (
                      <li key={it.to}>
                        <Link
                          to={it.to}
                          onMouseEnter={() => setActiveIdx(i)}
                          className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-[15px] font-medium transition-colors ${
                            i === activeIdx
                              ? 'bg-black/5 text-slate-900 dark:bg-white/10 dark:text-white'
                              : 'text-slate-600 hover:bg-black/[0.03] dark:text-slate-300 dark:hover:bg-white/5'
                          }`}
                        >
                          <span className="leading-snug">{it.label}</span>
                          <RightOutlined className="flex-none text-[11px] opacity-50" />
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Sağ detay */}
                  <div className="flex flex-col px-5 py-4">
                    <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                      {activeItem.label}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-500 dark:text-slate-300/80">
                      {activeItem.desc}
                    </p>

                    {/* Ideathon / Hackathon / Çalıştay → sektör alt kartları */}
                    {SUBCARDS[activeItem.to] && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {SUBCARDS[activeItem.to].map((p) => (
                          <Link
                            key={p.to}
                            to={p.to}
                            className="group/card flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] p-3 text-center transition-all hover:-translate-y-0.5"
                            style={{
                              boxShadow:
                                'inset 0 0 0 1px rgba(61,132,255,0.13)',
                            }}
                          >
                            {p.logo ? (
                              <span className="flex items-center gap-2.5">
                                <img
                                  src={p.logo}
                                  alt={pick(p.label, lang)}
                                  {...NAV_LOGO_IMAGE_PROPS}
                                  className="block h-9 max-w-full object-contain"
                                />
                                {p.withLabel && (
                                  <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                    {pick(p.label, lang)}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                {pick(p.label, lang)}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Kapsül Destek Programları → hangi programsa logosuyla kart */}
                    {activeItem.to === '/programs/support' && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {SUPPORT_PROGRAMS.map((p) => (
                          <Link
                            key={p.to}
                            to={p.to}
                            className="group/card flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-center transition-all hover:-translate-y-0.5"
                            style={{
                              boxShadow: `inset 0 0 0 1px ${p.accent}22`,
                            }}
                          >
                            <img
                              src={p.logo}
                              alt={pick(p.label, lang)}
                              {...NAV_LOGO_IMAGE_PROPS}
                              className={
                                p.wordmark
                                  ? 'block h-7 w-full object-contain'
                                  : 'block h-9 max-w-full object-contain'
                              }
                            />
                            {!p.wordmark && (
                              <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                {pick(p.label, lang)}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Odak Alanları → logosu olan programlar Destek stilinde kart */}
                    {activeItem.to === '/programs/focus' && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {focusPrograms.map((p) => {
                          // Amblem logolu programlarda (ör. Tarım Teknolojileri)
                          // ad logonun SAĞINDA yazılır → kart satır düzenine geçer.
                          const withLabel = LOGO_WITH_LABEL.includes(p.slug);
                          return (
                            <Link
                              key={p.to}
                              to={p.to}
                              className={`group/card flex rounded-xl border border-white/10 bg-white/[0.04] p-3 transition-all hover:-translate-y-0.5 ${
                                withLabel
                                  ? 'items-center gap-2 text-left'
                                  : 'flex-col items-center justify-center gap-2 text-center'
                              }`}
                              style={{
                                boxShadow:
                                  'inset 0 0 0 1px rgba(61,132,255,0.13)',
                              }}
                            >
                              {PROGRAM_LOGOS[p.slug] ? (
                                <>
                                  <img
                                    src={PROGRAM_LOGOS[p.slug]}
                                    alt={p.label}
                                    {...NAV_LOGO_IMAGE_PROPS}
                                    className={
                                      withLabel
                                        ? 'block h-8 w-auto flex-none object-contain'
                                        : 'block h-11 max-w-full object-contain'
                                    }
                                  />
                                  {withLabel && (
                                    <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                      {p.label}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                  {p.label}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Kapsül Gelişim Programları → Yetenek/Yetkinlik gruplu kartlar */}
                    {activeItem.to === '/programs/development' && (
                      <div className="mt-4 space-y-3">
                        {devGroups.map((g) => (
                          <div key={g}>
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-kred">
                              {g}
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                              {devPrograms
                                .filter(
                                  (p) => p.group && pick(p.group, lang) === g,
                                )
                                .map((p) => (
                                  <Link
                                    key={p.slug}
                                    to={`/program/${p.slug}`}
                                    className="group/card flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-center transition-all hover:-translate-y-0.5"
                                    style={{
                                      boxShadow:
                                        'inset 0 0 0 1px rgba(61,132,255,0.13)',
                                    }}
                                  >
                                    {PROGRAM_LOGOS[p.slug] ? (
                                      <img
                                        src={PROGRAM_LOGOS[p.slug]}
                                        alt={pick(p.title, lang)}
                                        {...NAV_LOGO_IMAGE_PROPS}
                                        className="block h-11 max-w-full object-contain"
                                      />
                                    ) : (
                                      <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                        {pick(p.title, lang)}
                                      </span>
                                    )}
                                  </Link>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Kapsül Radar → logolu tanıtım kartı */}
                    {activeItem.to === '/radar' && (
                      <div className="mt-4">
                        <Link
                          to="/radar"
                          className="group/card flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-4 text-center transition-all hover:-translate-y-0.5"
                          style={{
                            boxShadow: 'inset 0 0 0 1px rgba(190,30,45,0.25)',
                          }}
                        >
                          <img
                            src="/kapsul-radar.svg"
                            alt="Kapsül Radar"
                            {...NAV_LOGO_IMAGE_PROPS}
                            className="block h-12 max-w-full object-contain"
                          />
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* ── Mobil açılır menü — masaüstü mega-menü gibi adanın TAM
                altında (top-full) açılan cam panel. ── */}
            {open && (
              <div className="absolute left-0 top-full z-50 w-full pt-3 lg:hidden">
                <div
                  style={tintPanel}
                  className={`animate-pagefade max-h-[calc(100lvh-7rem)] w-full overflow-y-auto rounded-[1.25rem] border ${dropdownSurface} p-2.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),0_24px_60px_-16px_rgba(0,0,0,0.65)] backdrop-blur-2xl backdrop-saturate-150`}
                >
                  {/* Üst başlıklar — masaüstü panelin sol listesi tarzında TAM satır;
                seçilenin altında alt öğeler açılır. */}
                  <ul className="flex flex-col gap-0.5">
                    {/* Kurumsal — tek doğrudan link (açılır menü yok) */}
                    <li>
                      <Link
                        to="/institutional"
                        className="flex w-full items-center rounded-xl px-4 py-2.5 text-[15px] font-semibold text-slate-600 transition-colors hover:bg-black/[0.03] dark:text-slate-300 dark:hover:bg-white/5"
                      >
                        {t('nav.corporate')}
                      </Link>
                    </li>
                    {menus.map((m) => {
                      const isSec = mobileKey === m.key;
                      return (
                        <li key={m.key}>
                          <button
                            type="button"
                            onClick={() => setMobileKey(isSec ? null : m.key)}
                            aria-expanded={isSec}
                            className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-left text-[15px] font-semibold transition-colors ${
                              isSec
                                ? 'bg-black/5 text-slate-900 dark:bg-white/10 dark:text-white'
                                : 'text-slate-600 hover:bg-black/[0.03] dark:text-slate-300 dark:hover:bg-white/5'
                            }`}
                          >
                            <span className="leading-snug">{m.label}</span>
                            <DownOutlined
                              className={`flex-none text-[11px] opacity-50 transition-transform duration-200 ${
                                isSec ? 'rotate-180' : ''
                              }`}
                            />
                          </button>

                          {/* Seçili bölümün alt öğeleri — hemen başlığın altında,
                        hafif içeriden; masaüstü sol listesiyle aynı satırlar. */}
                          {isSec && (
                            <ul className="flex flex-col gap-0.5 py-0.5 pl-2">
                              {m.items.map((it) => {
                                // Kart ızgarası olan alt öğeler (alt kategorili):
                                // kendileri de açılır/kapanır ve ok taşır.
                                const hasCards =
                                  [
                                    '/programs/development',
                                    '/programs/support',
                                    '/programs/focus',
                                  ].includes(it.to) || Boolean(SUBCARDS[it.to]);
                                const isSub = mobileSubKey === it.to;

                                if (!hasCards)
                                  return (
                                    <li key={it.to}>
                                      <Link
                                        to={it.to}
                                        className="block rounded-lg px-4 py-2 text-sm text-slate-500 transition-colors hover:text-azure dark:text-slate-400 dark:hover:text-azure"
                                      >
                                        {it.label}
                                      </Link>
                                    </li>
                                  );

                                return (
                                  <li key={it.to}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setMobileSubKey(isSub ? null : it.to)
                                      }
                                      aria-expanded={isSub}
                                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-4 py-2 text-left text-sm transition-colors ${
                                        isSub
                                          ? 'text-azure'
                                          : 'text-slate-500 hover:text-azure dark:text-slate-400 dark:hover:text-azure'
                                      }`}
                                    >
                                      {it.label}
                                      <DownOutlined
                                        className={`flex-none text-[10px] opacity-50 transition-transform duration-200 ${
                                          isSub ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </button>

                                    {/* Ideathon / Hackathon / Çalıştay → alt kartlar */}
                                    {isSub && SUBCARDS[it.to] && (
                                      <div className="mb-1.5 grid grid-cols-2 gap-2 px-4 pt-1">
                                        {SUBCARDS[it.to].map((p) => (
                                          <Link
                                            key={p.to}
                                            to={p.to}
                                            className="group/card flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-center transition-all hover:-translate-y-0.5"
                                            style={{
                                              boxShadow:
                                                'inset 0 0 0 1px rgba(61,132,255,0.13)',
                                            }}
                                          >
                                            {p.logo ? (
                                              <span className="flex items-center gap-2">
                                                <img
                                                  src={p.logo}
                                                  alt={pick(p.label, lang)}
                                                  {...NAV_LOGO_IMAGE_PROPS}
                                                  // Destek kartlarıyla aynı ölçek:
                                                  // söz logosu h-6 (+max-w-full),
                                                  // amblem + ad ise h-8.
                                                  className={
                                                    p.withLabel
                                                      ? 'block h-8 w-auto flex-none object-contain'
                                                      : 'block h-6 max-w-full object-contain'
                                                  }
                                                />
                                                {p.withLabel && (
                                                  <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                    {pick(p.label, lang)}
                                                  </span>
                                                )}
                                              </span>
                                            ) : (
                                              <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                {pick(p.label, lang)}
                                              </span>
                                            )}
                                          </Link>
                                        ))}
                                      </div>
                                    )}

                                    {/* Odak Alanları → logolu kartlar */}
                                    {isSub && it.to === '/programs/focus' && (
                                      <div className="mb-1.5 grid grid-cols-2 gap-2 px-4 pt-1">
                                        {focusPrograms.map((p) => {
                                          // Masaüstündeki gibi: amblem logolu
                                          // programda ad logonun SAĞINDA durur.
                                          const withLabel =
                                            LOGO_WITH_LABEL.includes(p.slug);
                                          return (
                                            <Link
                                              key={p.to}
                                              to={p.to}
                                              className={`group/card flex rounded-xl border border-white/10 bg-white/[0.04] p-2.5 transition-all hover:-translate-y-0.5 ${
                                                withLabel
                                                  ? 'items-center gap-2 text-left'
                                                  : 'flex-col items-center justify-center gap-1.5 text-center'
                                              }`}
                                              style={{
                                                boxShadow:
                                                  'inset 0 0 0 1px rgba(61,132,255,0.13)',
                                              }}
                                            >
                                              {PROGRAM_LOGOS[p.slug] ? (
                                                <>
                                                  <img
                                                    src={PROGRAM_LOGOS[p.slug]}
                                                    alt={p.label}
                                                    {...NAV_LOGO_IMAGE_PROPS}
                                                    // Destek kartlarıyla aynı ölçek.
                                                    className={
                                                      withLabel
                                                        ? 'block h-8 w-auto flex-none object-contain'
                                                        : 'block h-6 max-w-full object-contain'
                                                    }
                                                  />
                                                  {withLabel && (
                                                    <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                      {p.label}
                                                    </span>
                                                  )}
                                                </>
                                              ) : (
                                                <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                  {p.label}
                                                </span>
                                              )}
                                            </Link>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Kapsül Gelişim → Yetenek/Yetkinlik gruplu kartlar */}
                                    {isSub &&
                                      it.to === '/programs/development' && (
                                        <div className="mb-1.5 space-y-2 px-4 pt-1">
                                          {devGroups.map((g) => (
                                            <div key={g}>
                                              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-kred">
                                                {g}
                                              </p>
                                              <div className="grid grid-cols-2 gap-2">
                                                {devPrograms
                                                  .filter(
                                                    (p) =>
                                                      p.group &&
                                                      pick(p.group, lang) === g,
                                                  )
                                                  .map((p) => (
                                                    <Link
                                                      key={p.slug}
                                                      to={`/program/${p.slug}`}
                                                      className="group/card flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-center transition-all hover:-translate-y-0.5"
                                                      style={{
                                                        boxShadow:
                                                          'inset 0 0 0 1px rgba(61,132,255,0.13)',
                                                      }}
                                                    >
                                                      {/* Masaüstü panelle aynı: logosu olan
                                                          program logosuyla gösterilir —
                                                          ölçek Destek kartlarıyla ortak. */}
                                                      {PROGRAM_LOGOS[p.slug] ? (
                                                        <img
                                                          src={
                                                            PROGRAM_LOGOS[
                                                              p.slug
                                                            ]
                                                          }
                                                          alt={pick(
                                                            p.title,
                                                            lang,
                                                          )}
                                                          {...NAV_LOGO_IMAGE_PROPS}
                                                          className="block h-6 max-w-full object-contain"
                                                        />
                                                      ) : (
                                                        <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                          {pick(p.title, lang)}
                                                        </span>
                                                      )}
                                                    </Link>
                                                  ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                    {/* Kapsül Destek Programları → logolu kartlar */}
                                    {isSub && it.to === '/programs/support' && (
                                      <div className="mb-1.5 grid grid-cols-3 gap-2 px-4 pt-1">
                                        {SUPPORT_PROGRAMS.map((p) => (
                                          <Link
                                            key={p.to}
                                            to={p.to}
                                            className="group/card flex min-h-[3.5rem] flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-slate-950/35 p-2 text-center transition-all hover:-translate-y-0.5"
                                            style={{
                                              boxShadow: `inset 0 0 0 1px ${p.accent}22`,
                                            }}
                                          >
                                            <img
                                              src={p.logo}
                                              alt={pick(p.label, lang)}
                                              {...NAV_LOGO_IMAGE_PROPS}
                                              className={
                                                p.wordmark
                                                  ? 'block h-5 w-full object-contain min-[380px]:h-6'
                                                  : 'block h-8 max-w-full object-contain'
                                              }
                                            />
                                            {!p.wordmark && (
                                              <span className="text-[10px] font-medium leading-tight text-slate-600 dark:text-slate-300">
                                                {pick(p.label, lang)}
                                              </span>
                                            )}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {/* 2027 Yarışma Başvuruları — masaüstündeki kırmızı CTA.
                      Liste satırlarıyla AYNI dış hiza (ek yatay padding yok). */}
                  <div className="mt-1">
                    <a
                      href={TEAM_APPLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 rounded-xl border-2 border-kred bg-kred/[0.06] px-4 py-2.5 transition-all hover:bg-kred/10"
                    >
                      <span className="text-[20px] font-extrabold leading-none tracking-tight text-kred">
                        {t('nav.applyYear')}
                      </span>
                      <span className="flex flex-col text-[9px] font-bold uppercase leading-[1.15] tracking-wide text-kred">
                        {t('nav.applyTitle')
                          .split(' ')
                          .map((w) => (
                            <span key={w}>{w}</span>
                          ))}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
